import { useEffect, useMemo, useRef, useState } from 'react';
import { Pagination, Combobox, useCombobox, InputBase, Group } from '@mantine/core';
import './styles.scss';

interface PaginationProps {
  total: number | undefined;
  itemsPerPage: number;
  /** the *current* dynamic limit calculated by your hook (pass it from parent!) */
  dynamicLimit: number;
  onPageChange: (page: number) => void;
  onResultsChange: (newValue: number) => void;
  currentPage?: number;
  offset?: number;
}

const PaginationComponent: React.FC<PaginationProps> = ({
  total = 0,
  itemsPerPage,
  dynamicLimit,
  onPageChange,
  onResultsChange,
  currentPage
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const comboBox = useCombobox();
  const initialItemsPerPageRef = useRef(itemsPerPage);
  const values = useMemo(() => {
    const base = [10, 12, 25, 50, 100].map(v => (v === 12 ? dynamicLimit : v));
    if (!base.includes(itemsPerPage)) base.push(itemsPerPage);
    if (!base.includes(initialItemsPerPageRef.current)) {
      base.push(initialItemsPerPageRef.current);
    }
    return Array.from(new Set(base))
      .filter(v => v > 0)
      .sort((a, b) => a - b);
  }, [dynamicLimit, itemsPerPage]);

  const [value, setValue] = useState(itemsPerPage);

  useEffect(() => {
    if (total && total > 0 && itemsPerPage > 0) {
      setTotalPages(Math.ceil(total / itemsPerPage));
    } else {
      setTotalPages(1);
    }
  }, [total, itemsPerPage]);

  useEffect(() => {
    setValue(itemsPerPage);
  }, [itemsPerPage]);

  const handleItemsPerPageChange = (newValue: string) => {
    const parsedValue = parseInt(newValue, 10);
    setValue(parsedValue);
    onResultsChange(parsedValue);
    comboBox.closeDropdown();
  };

  return (
    <Group classNames={{ root: 'pagination' }} style={{ justifyContent: 'flex-end' }}>
      <Pagination radius="md" withEdges total={totalPages} value={currentPage ?? 1} onChange={onPageChange} />

      <Combobox
        classNames={{ dropdown: 'showValues' }}
        store={comboBox}
        withinPortal={false}
        onOptionSubmit={handleItemsPerPageChange}
      >
        <Combobox.Target>
          <InputBase
            component="button"
            type="button"
            pointer
            rightSection={<Combobox.Chevron />}
            onClick={() => comboBox.toggleDropdown()}
            rightSectionPointerEvents="none"
          >
            {value}
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {values.map(v => (
              <Combobox.Option value={v.toString()} key={v}>
                {v}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Group>
  );
};

export default PaginationComponent;
